import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);

  useEffect(() => {
    // Check if URL contains hash parameters typical of a recovery flow or if there is an active session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      const hasRecoveryHash = window.location.hash.includes('access_token') && 
                              (window.location.hash.includes('type=recovery') || window.location.hash.includes('type=signup'));
      
      if (session || hasRecoveryHash) {
        setIsRecoveryMode(true);
      }
    };

    checkSession();

    // Listen to Auth State Changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryMode(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Por favor, informe seu e-mail para darmos início à recuperação.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) throw resetError;

      setSuccessMsg('E-mail de recuperação enviado com sucesso! Verifique sua caixa de entrada.');
    } catch (err: any) {
      console.error('Password reset request error:', err);
      setError(err.message || 'Erro ao tentar enviar o e-mail de recuperação.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!password) {
      setError('Por favor, digite sua nova senha.');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve conter no mínimo 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem. Verifique a digitação.');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      setSuccessMsg('Senha alterada com sucesso! Você agora poderá fazer login.');
      
      // Auto logout to clean active temporary recovery session and navigate to login
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // Ignorar erros de rede no encerramento de sessão temporária
      }
      localStorage.removeItem('session_confirmed');

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2500);

    } catch (err: any) {
      console.error('Password update error:', err);
      setError(err.message || 'Erro ao atualizar sua senha. O link de recuperação pode ter expirado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-slate-950">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="max-w-md w-full space-y-6 bg-slate-900/40 p-6 sm:p-10 rounded-[2rem] sm:rounded-[2.5rem] border border-slate-800/50 backdrop-blur-2xl shadow-3xl relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent opacity-50"></div>
        
        <div>
          <div className="flex justify-center">
            <motion.div 
              whileHover={{ rotate: 5, scale: 1.05 }}
              className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-purple-600/20 mb-6"
            >
              <i className="fas fa-key text-2xl text-white"></i>
            </motion.div>
          </div>
          <h2 className="text-center text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight">
            {isRecoveryMode ? 'Nova Senha' : 'Recuperar Senha'}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Cardumy</span>
          </h2>
          <p className="mt-2 text-center text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em]">
            {isRecoveryMode ? 'Defina sua nova credencial de segurança' : 'Siga as orientações para redefinir'}
          </p>
        </div>

        {error && (
          <motion.div 
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="p-4 rounded-2xl text-[10px] sm:text-xs font-bold border bg-red-500/10 border-red-500/30 text-red-400"
          >
            <div className="flex gap-3">
              <i className="fas fa-circle-exclamation mt-0.5"></i>
              <span>{error}</span>
            </div>
          </motion.div>
        )}

        {successMsg && (
          <motion.div 
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="p-4 rounded-2xl text-[10px] sm:text-xs font-bold border bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
          >
            <div className="flex gap-3">
              <i className="fas fa-circle-check mt-0.5"></i>
              <span>{successMsg}</span>
            </div>
          </motion.div>
        )}

        {!isRecoveryMode ? (
          <form className="space-y-4" onSubmit={handleRequestReset}>
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
                E-mail de Cadastro
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                  <i className="fas fa-envelope text-xs"></i>
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800/80 rounded-2xl text-slate-300 text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition duration-300 font-sans"
                  placeholder="exemplo@gmail.com"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full relative py-3.5 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-black uppercase text-[10px] tracking-[0.25em] rounded-2xl hover:from-purple-500 hover:to-pink-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-950 transition-all duration-300 shadow-lg shadow-purple-600/20 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-spinner animate-spin"></i>
                  Enviando...
                </span>
              ) : (
                'Enviar Link de Recuperação'
              )}
            </button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={handleUpdatePassword}>
            <div className="space-y-1.5">
              <label htmlFor="password" className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
                Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                  <i className="fas fa-lock text-xs"></i>
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800/80 rounded-2xl text-slate-300 text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition duration-300"
                  placeholder="Defina sua nova senha"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="confirmPassword" className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
                Confirmar Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                  <i className="fas fa-lock text-xs"></i>
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-800/80 rounded-2xl text-slate-300 text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition duration-300"
                  placeholder="Confirme a nova senha"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full relative py-3.5 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-black uppercase text-[10px] tracking-[0.25em] rounded-2xl hover:from-purple-500 hover:to-pink-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-950 transition-all duration-300 shadow-lg shadow-purple-600/20 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-spinner animate-spin"></i>
                  Salvando nova senha...
                </span>
              ) : (
                'Salvar Nova Senha'
              )}
            </button>
          </form>
        )}

        <div className="flex justify-between items-center pt-2 border-t border-slate-800/50">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-white transition duration-300 flex items-center gap-2"
          >
            <i className="fas fa-arrow-left text-[8px] text-purple-400"></i>
            Voltar para o Login
          </button>
        </div>
      </motion.div>
    </div>
  );
};
export default ResetPassword;
